import { type ReactElement } from 'react';
import { Link, type LinkProps } from 'react-router-dom';

import classNames from 'classnames';

import styles from './Button.module.css';

import { PlusBadge } from '../../icons/PlusBadge';
import { PlusBadgeDisabled } from '../../icons/PlusBadgeDisabled';
import { PlusBadgeInvert } from '../../icons/PlusBadgeInvert';
import type { ActivityIndicatorProps } from '../ActivityIndicator';
import { ActivityIndicator } from '../ActivityIndicator';
import { ButtonBase, type ButtonBaseProps } from '../ButtonBase';

export type ButtonVariant =
  | 'primary'
  | 'primary-mini'
  | 'secondary'
  | 'secondary-mini'
  | 'special'
  | 'plain-text'
  | 'plain-text-mini';

export type ButtonFoundationVariant = 'solid' | 'outline';
export type ButtonFoundationSize = 'sm' | 'md' | 'lg';

/**
 * Icon keys supported by the foundation `Button` `withIcon` prop. The icon
 * element and its size are resolved internally — consumers supply only the key.
 * Extend this union as new icon variants are added internally.
 */
export type ButtonWithIcon = 'plus';

/** The types of a button supported by {@link Button} */
export type ButtonType = 'button' | 'submit' | 'reset' | 'anchor' | 'link';

export type ButtonSize = 'sm' | 'md' | 'lg';
export type ButtonWeight = 'bold' | 'normal';
export type ButtonColor =
  | 'carrot'
  | 'kale'
  | 'white'
  | 'kale-dark'
  | 'error-red-dark';

/** The props of {@link Button} when `type="anchor"`. */
export type ButtonAnchorProps = JSX.IntrinsicElements['a'] & {
  /** Required. The URL the anchor should point to. */
  href: string;
};

/**
 * The props of {@link Button} when `type="link"`. Extends react-router-dom
 * `LinkProps`.
 */
export type ButtonLinkProps = LinkProps &
  Omit<JSX.IntrinsicElements['a'], 'href' | 'ref'>; // Passing IntrinsicElement and removing `ref` is required to avoid type error.

interface LegacyButtonSharedProps<
  T extends ButtonType | undefined,
  V extends ButtonVariant | undefined,
> {
  /**
   * The type of the button.
   *
   * - To render a React Router Link, use `type="link"` and pass a `to` prop.
   * - To render an HTML anchor (`<a>`), use `type="anchor"` and pass a `href`
   *   prop.
   * - When type is `button`, `submit` or `reset`, the button will implement a
   *   styled {@link ButtonBase}.
   *
   * @defaultValue `button`
   */
  type?: T;
  /**
   * Change the appearance of the button.
   *
   * @deprecated Use `variant="solid"` or `variant="outline"` with `size` and
   *   `invert` props.
   * @defaultValue `primary`
   */
  variant?: V;
  /**
   * Toggle the loading status showing the {@link ActivityIndicator}. When
   * `true`, the `aria-busy` attribute will be set to `true`.
   */
  loading?: boolean;
  /** The aria-label when showing the {@link ActivityIndicator}. */
  loadingLabel?: ActivityIndicatorProps['aria-label'];
  /** Render the button as disabled. */
  disabled?: T extends 'link' | 'anchor' ? never : boolean;

  /** @deprecated Used for demo purpose only. */
  active?: boolean;
  /** @deprecated Used for demo purpose only. */
  hover?: boolean;

  /** Add the underline decoration when {@link variant} is `plain-text`. */
  underline?: V extends 'plain-text' ? boolean : never;
  /**
   * Set the font weight when {@link variant} is `plain-text`.
   *
   * @defaultValue 'bold'
   */
  weight?: V extends 'plain-text' ? ButtonWeight : never;
  /**
   * Set the accent color when {@link variant} is `plain-text`.
   *
   * @defaultValue 'carrot'
   */
  color?: V extends 'plain-text' ? ButtonColor : never;
  /**
   * Set the font size when {@link variant} is `plain-text`.
   *
   * @defaultValue 'md'
   */
  size?: V extends 'plain-text' ? ButtonSize : never;
  /** Set a custom icon when {@link variant} is `plain-text-mini`. */
  icon?: V extends 'plain-text-mini' ? ReactElement : never;
}

interface ButtonFoundationBaseProps<T extends ButtonType | undefined> {
  /**
   * The type of the button.
   *
   * - To render a React Router Link, use `type="link"` and pass a `to` prop.
   * - To render an HTML anchor (`<a>`), use `type="anchor"` and pass a `href`
   *   prop.
   * - When type is `button`, `submit` or `reset`, the button will implement a
   *   styled {@link ButtonBase}.
   *
   * @defaultValue `button`
   */
  type?: T;
  /**
   * Flip to the theme's inverse action color. Use when two differently-colored
   * buttons appear in the same container.
   */
  invert?: boolean;
  /** Render the button as disabled. */
  disabled?: T extends 'link' | 'anchor' ? never : boolean;

  /** @deprecated Used for demo purpose only. */
  active?: boolean;
  /** @deprecated Used for demo purpose only. */
  hover?: boolean;

  /**
   * Explicitly excluded. `color` is a legacy plain-text prop that is also a
   * deprecated HTML attribute — without this exclusion TypeScript would allow
   * it via ButtonBaseProps when the new branch is active.
   */
  color?: never;
}

interface ButtonFoundationSmProps<
  T extends ButtonType | undefined,
> extends ButtonFoundationBaseProps<T> {
  size: 'sm';
  variant: ButtonFoundationVariant;
  /** Not supported at `size="sm"`. */
  loading?: never;
  /** Not supported at `size="sm"`. */
  loadingLabel?: never;
}

interface ButtonFoundationMdProps<
  T extends ButtonType | undefined,
> extends ButtonFoundationBaseProps<T> {
  size: 'md';
  variant: ButtonFoundationVariant;
  /**
   * Toggle the loading status showing the {@link ActivityIndicator}. When
   * `true`, the `aria-busy` attribute will be set to `true`.
   */
  loading?: boolean;
  /** The aria-label when showing the {@link ActivityIndicator}. */
  loadingLabel?: ActivityIndicatorProps['aria-label'];
  /**
   * When provided, renders a trailing circular contrast badge containing an
   * internally-resolved icon. The badge color swaps with `invert`.
   */
  withIcon?: ButtonWithIcon;
}

interface ButtonFoundationLgProps<
  T extends ButtonType | undefined,
> extends ButtonFoundationBaseProps<T> {
  size: 'lg';
  variant: 'solid';
  /**
   * Toggle the loading status showing the {@link ActivityIndicator}. When
   * `true`, the `aria-busy` attribute will be set to `true`.
   */
  loading?: boolean;
  /** The aria-label when showing the {@link ActivityIndicator}. */
  loadingLabel?: ActivityIndicatorProps['aria-label'];
}

type ButtonFoundationSharedProps<T extends ButtonType | undefined> =
  | ButtonFoundationSmProps<T>
  | ButtonFoundationMdProps<T>
  | ButtonFoundationLgProps<T>;

type ButtonElementProps<T extends ButtonType | undefined> = T extends 'link'
  ? ButtonLinkProps
  : T extends 'anchor'
    ? ButtonAnchorProps
    : ButtonBaseProps;

type ButtonFoundationProps<T extends ButtonType | undefined> =
  ButtonFoundationSharedProps<T> & ButtonElementProps<T>;

/**
 * Legacy-only button props (excludes foundation variants). Use when a component
 * always renders a legacy-variant button and needs to accept pass-through
 * button props without admitting foundation-specific props.
 */
export type LegacyButtonProps<
  T extends ButtonType | undefined = 'button',
  V extends ButtonVariant | undefined = undefined,
> = LegacyButtonSharedProps<T, V> & ButtonElementProps<T>;

export type ButtonSharedProps<
  T extends ButtonType | undefined,
  V extends ButtonVariant | undefined,
> = LegacyButtonSharedProps<T, V> | ButtonFoundationSharedProps<T>;

export type ButtonProps<
  T extends ButtonType | undefined = 'button',
  V extends ButtonVariant | undefined = 'primary',
> = ButtonSharedProps<T, V> & ButtonElementProps<T>;

function resolveWithIconElement(
  key: ButtonWithIcon,
  { invert, isDisabled }: { invert: boolean; isDisabled: boolean }
): ReactElement | null {
  if (key === 'plus') {
    if (isDisabled) {
      return <PlusBadgeDisabled aria-hidden="true" />;
    }
    if (invert) {
      // On invert buttons the button background flips on hover/active, so the
      // badge needs to flip too. Render both icons and let CSS swap visibility.
      return (
        <>
          <span className={styles.withIconRest}>
            <PlusBadgeInvert aria-hidden="true" />
          </span>
          <span className={styles.withIconHover}>
            <PlusBadge aria-hidden="true" />
          </span>
        </>
      );
    }
    return <PlusBadge aria-hidden="true" />;
  }
  return null;
}

function isButtonFoundationVariant(
  variant: string | undefined
): variant is ButtonFoundationVariant {
  return variant === 'solid' || variant === 'outline';
}

function isButtonFoundationProps<
  T extends ButtonType | undefined,
  V extends ButtonVariant | undefined,
>(props: ButtonProps<T, V>): props is ButtonFoundationProps<T> {
  return isButtonFoundationVariant(props.variant);
}

function isLegacyButtonProps<
  T extends ButtonType | undefined,
  V extends ButtonVariant | undefined,
>(props: ButtonProps<T, V>): props is LegacyButtonProps<T, V> {
  return !isButtonFoundationVariant(props.variant);
}

/**
 * Render a button or a link with the style of the specified variant.
 *
 * @cssVariable --tfd-button-color The main color for the button background (primary variants) or text (secondary variants). Typically this is non-white for light backgrounds, or else white for dark backgrounds.
 * @cssVariable --tfd-button-active-color The color to use for the background or text when the button is active or hovered
 * @cssVariable --tfd-button-contrast-color The color that contrasts with the main button color. Defaults to white for light backgrounds.
 * @cssVariable --tfd-button-loading-color The color to use for the background or text when the button is loading
 * @cssVariable --tfd-button-border-radius The radius of the button border
 * @cssVariable --tfd-button-border-primary The border of the primary and primary-mini buttons
 * @cssVariable --tfd-button-border-secondary The border of the secondary and secondary-mini buttons
 * @cssVariable --tfd-button-regular-font The default font settings for primary/secondary buttons
 * @cssVariable --tfd-button-regular-letter-spacing The letter-spacing to use for regular primary/secondary buttons
 * @cssVariable --tfd-button-mini-font The default font settings for all primary-mini/secondary-mini/plain-text-mini buttons
 * @cssVariable --tfd-button-mini-letter-spacing The letter-spacing to use for the buttons with mini variants
 * @cssVariable --tfd-button-mini-text-transform The text transform (e.g. uppercase) to use for the buttons with mini variants
 * @cssVariable --tfd-button-padding-primary The padding around the primary button variant
 * @cssVariable --tfd-button-padding-primary-mini The padding around the primary-mini button variant
 * @cssVariable --tfd-button-padding-secondary The padding around the secpndary button variant
 * @cssVariable --tfd-button-padding-secondary-mini The padding around the secpndary-mini button variant
 * @cssVariable --tfd-button-plain-text-decoration The text decoration of plain-text buttons (e.g. underline)
 * @cssVariable --tfd-button-plain-text-font The font family, weight, size, and line-height of plain-text buttons
 * @cssVariable --tfd-button-plain-text-letter-spacing The letter spacing for plain-text buttons
 * @see https://corgi-x.tfd.engineering/components/button
 */
export function Button<
  T extends ButtonType | undefined = 'button',
  V extends ButtonVariant | undefined = 'primary',
>(props: ButtonProps<T, V>) {
  const {
    // Extracted to prevent Button-only props from being spread onto the HTML element
    variant: _variant,
    size: _size,
    color: _color,
    invert: _invert,
    underline: _underline,
    weight: _weight,
    icon: _icon,
    withIcon: _withIcon,
    loading = false,
    type = 'button',
    className,
    active = false,
    hover = false,
    disabled,
    children,
    loadingLabel,
    'aria-disabled': ariaDisabled,
    'aria-busy': ariaBusy,
    ...elementProps
  } = props as ButtonProps<T, V> & {
    invert?: boolean;
    underline?: boolean;
    weight?: ButtonWeight;
    icon?: ReactElement;
    withIcon?: ButtonWithIcon;
  };

  let isFoundation = false;
  let variantProp: ButtonVariant | ButtonFoundationVariant = 'primary';
  let size: ButtonSize | ButtonFoundationSize | undefined;
  let invert = false;
  let color: ButtonColor | undefined;
  let underline = false;
  let weight: ButtonWeight | undefined;
  let icon: ReactElement | undefined;
  let withIcon: ButtonWithIcon | undefined;

  if (isButtonFoundationProps(props)) {
    isFoundation = true;
    variantProp = props.variant;
    size = props.size;
    invert = props.invert ?? false;
    if (props.size === 'md') {
      withIcon = props.withIcon;
    }
  } else if (isLegacyButtonProps(props)) {
    variantProp = props.variant ?? 'primary';
    size = props.size;
    color = props.color;
    underline = props.underline ?? false;
    weight = props.weight;
    icon = props.icon;
  }

  const cssModifiers = {
    [styles.active]: active,
    [styles.hover]: hover,
    [styles.loading]: loading,
    [styles.disabled]: ariaDisabled || disabled,
  };

  // Legacy-only font modifier classes (plain-text variant)
  const cssLegacyFont = !isFoundation
    ? {
        [styles.underline]: underline,
        [styles.bold]: weight === 'bold',
        [styles.normal]: weight === 'normal',
      }
    : {};

  const cssClass = classNames(
    styles.button,
    styles[variantProp],
    size && styles[size],
    isFoundation && invert && styles.invert,
    isFoundation && withIcon && styles.hasIcon,
    !isFoundation && color && styles[color],
    cssLegacyFont,
    cssModifiers,
    className
  );

  /**
   * Used when `aria-disabled` is `true` and `type="link"` or `type="anchor"` to
   * prevent the button from being clicked.
   */
  const handleAriaDisabledClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const activityIndicatorColor = !isFoundation
    ? variantProp === 'plain-text'
      ? color
      : 'white'
    : invert
      ? 'kale'
      : undefined;

  const buttonContent = (
    <>
      {!isFoundation && variantProp === 'plain-text-mini' && icon ? (
        <>
          {children}
          {icon}
        </>
      ) : (
        children
      )}
      {withIcon && (
        <span className={styles.withIcon} data-tfd-button-with-icon>
          {resolveWithIconElement(withIcon, {
            invert,
            isDisabled: Boolean(disabled || ariaDisabled),
          })}
        </span>
      )}
      {loading && (
        <span className={styles.indicator}>
          <ActivityIndicator
            aria-label={loadingLabel}
            color={activityIndicatorColor}
          />
        </span>
      )}
    </>
  );

  /* Render an HTML anchor */
  if (type === 'anchor' && 'href' in elementProps) {
    const anchorProps = elementProps as ButtonAnchorProps;
    return (
      <a
        {...anchorProps}
        aria-busy={ariaBusy || loading || undefined}
        aria-disabled={ariaDisabled || undefined}
        className={cssClass}
        onClick={ariaDisabled ? handleAriaDisabledClick : anchorProps.onClick}
      >
        {buttonContent}
      </a>
    );
  }

  /* Render a Link */
  if (type === 'link' && 'to' in elementProps) {
    const linkProps = elementProps as ButtonLinkProps;
    return (
      <Link
        {...linkProps}
        aria-busy={ariaBusy || loading || undefined}
        aria-disabled={ariaDisabled || undefined}
        className={cssClass}
        onClick={ariaDisabled ? handleAriaDisabledClick : linkProps.onClick}
      >
        {buttonContent}
      </Link>
    );
  }

  /* Render a button */
  const baseProps = elementProps as ButtonBaseProps;
  return (
    <ButtonBase
      {...baseProps}
      type={type as ButtonBaseProps['type']}
      aria-busy={ariaBusy || loading || undefined}
      aria-disabled={
        ariaDisabled === true || loading === true ? true : undefined
      }
      className={cssClass}
      disabled={disabled}
    >
      {buttonContent}
    </ButtonBase>
  );
}
